import { useState, useContext } from "react";
import { FaFileDownload, FaTimes, FaDatabase } from "react-icons/fa";
import { downloadTaxonomy } from "./TaxonomySelector";
import { metadataService } from "../../../../../../../services/api";
import { toast } from "react-hot-toast";
import { BaseContext } from "../../../../../../../contexts/BaseContext";

export const TaxonomySaveModal = ({
  isOpen,
  onClose,
  valueMapping,
  tagsDetails,
}) => {
  const { deasyApiKey, vdbLastActiveProfileName } = useContext(BaseContext);
  const [taxonomyName, setTaxonomyName] = useState("");
  const [isSavingToJson, setIsSavingToJson] = useState(false);
  const [isSavingToLibrary, setIsSavingToLibrary] = useState(false);
  const [error, setError] = useState("");

  // Placeholder functions
  const saveAsJson = async (name) => {
    try {
      setIsSavingToJson(true);
      const taxonomy = {
        standard_mapping: valueMapping,
        taxonomy_name: name,
        tag_id: tagsDetails.tag_id,
        tag_name: tagsDetails.name,
        created_at: new Date().toISOString(),
      };
      downloadTaxonomy(taxonomy);
      toast.success("Taxonomy saved as JSON file");
    } catch (error) {
      setError("Failed to save taxonomy as JSON file");
    } finally {
      setIsSavingToJson(false);
    }
  };

  const saveToTaxonomyLibrary = async (name) => {
    try {
      setIsSavingToLibrary(true);
      await metadataService.insertStandardization({
        tag_id: tagsDetails.tag_id,
        tag_name: tagsDetails.name,
        standard_mapping: valueMapping,
        taxonomy_name: name,
        vdbProfileName: vdbLastActiveProfileName,
        deasyApiKey,
      });
      toast.success("Taxonomy saved to library");
    } catch (error) {
      setError("Failed to save taxonomy to library");
    } finally {
      setIsSavingToLibrary(false);
    }
  };

  const handleClose = () => {
    if (!isSavingToJson && !isSavingToLibrary) {
      setTaxonomyName("");
      setError("");
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Content */}
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSavingToJson || isSavingToLibrary}
          >
            <FaTimes size={20} />
          </button>

          {/* Modal Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">
              Save Taxonomy Definition
            </h2>
            <p className="text-gray-600 mt-1">
              Save your taxonomy configuration for future use (this does not
              apply it to your data)
            </p>
          </div>

          {/* Taxonomy Name Input */}
          <div className="mb-6">
            <label
              htmlFor="taxonomyName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Taxonomy Name
            </label>
            <input
              id="taxonomyName"
              type="text"
              value={taxonomyName}
              onChange={(e) => setTaxonomyName(e.target.value)}
              placeholder="Enter taxonomy name..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
              disabled={isSavingToJson || isSavingToLibrary}
            />
          </div>

          {/* Save Options */}
          <div className="mb-6 space-y-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Save Options
            </label>

            {/* Save as JSON Option */}
            <button
              onClick={() => saveAsJson(taxonomyName)}
              disabled={isSavingToJson || !taxonomyName}
              className={`w-full flex items-center p-3 border-2 rounded-lg transition-all ${
                isSavingToJson || !taxonomyName
                  ? "border-primary-500 bg-primary-50 shadow-sm"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              } ${isSavingToJson || !taxonomyName ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <FaFileDownload
                className={`mr-2 transition-colors ${
                  isSavingToJson || !taxonomyName
                    ? "text-primary-600"
                    : "text-gray-500"
                }`}
              />
              <div className="text-left">
                <span
                  className={`font-medium transition-colors ${
                    isSavingToJson || !taxonomyName
                      ? "text-primary-900"
                      : "text-gray-900"
                  }`}
                >
                  Save as JSON file
                </span>
                <p className="text-sm text-gray-500">
                  Download taxonomy as a JSON file
                </p>
              </div>
            </button>

            {/* Save to Library Option */}
            <button
              onClick={() => saveToTaxonomyLibrary(taxonomyName)}
              disabled={isSavingToLibrary || !taxonomyName}
              className={`w-full flex items-center p-3 border-2 rounded-lg transition-all ${
                isSavingToLibrary || !taxonomyName
                  ? "border-primary-500 bg-primary-50 shadow-sm"
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              } ${isSavingToLibrary || !taxonomyName ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <FaDatabase
                className={`mr-2 transition-colors ${
                  isSavingToLibrary || !taxonomyName
                    ? "text-primary-600"
                    : "text-gray-500"
                }`}
              />
              <div className="text-left">
                <span
                  className={`font-medium transition-colors ${
                    isSavingToLibrary || !taxonomyName
                      ? "text-primary-900"
                      : "text-gray-900"
                  }`}
                >
                  Save to Taxonomy Library
                </span>
                <p className="text-sm text-gray-500">
                  Store in your organization's library
                </p>
              </div>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
